/**
 * /departments/:deptKey — Department detail page.
 *
 * Tabs (URL-driven via ?tab=overview|queue|handoffs|escalations|settings):
 *   - Overview     KPI cards + recent activity timeline
 *   - Queue        paginated standing-queue items (priority sort)
 *   - Handoffs     incoming + outgoing recent handoffs
 *   - Escalations  open + recent escalations
 *   - Settings     read-only metadata + handoff contract list
 *
 * Real-time: useDepartmentEvents subscribes to /ws/departments/:deptId/events
 * (the deptId, NOT the slug, because the channel name uses the UUID).
 */
import type { DepartmentActivity, QueueItem } from '@chaos/types/department';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeftRight, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { z } from 'zod';

import { trackRecentDepartment } from '../../../components/departments/departments-sidebar';
import { EscalationForm } from '../../../components/departments/escalation-form';
import { HandoffForm } from '../../../components/departments/handoff-form';
import {
  fmtCount,
  fmtPercent,
  fmtRelative,
  KpiTile,
} from '../../../components/departments/kpi-tile';
import { StewardChatPane } from '../../../components/departments/steward-chat-pane';
import { Button } from '../../../components/ui/button';
import { departmentsApi, departmentsKeys } from '../../../lib/api/departments';
import { useDepartmentEvents } from '../../../lib/departments/use-department-events';
import { cn } from '../../../lib/utils';

const deptSearchSchema = z.object({
  tab: z.enum(['overview', 'queue', 'handoffs', 'escalations', 'settings']).default('overview'),
});

type DeptTab = z.infer<typeof deptSearchSchema>['tab'];

const TABS: { value: DeptTab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'queue', label: 'Queue' },
  { value: 'handoffs', label: 'Handoffs' },
  { value: 'escalations', label: 'Escalations' },
  { value: 'settings', label: 'Settings' },
];

const STATE_LABEL = {
  green: 'Healthy',
  yellow: 'Lagging',
  red: 'Failed',
  unknown: 'Unknown',
} as const;

const STATE_DOT = {
  green: 'bg-success',
  yellow: 'bg-warning',
  red: 'bg-destructive',
  unknown: 'bg-muted-foreground/40',
} as const;

function DepartmentDetailPage(): React.JSX.Element {
  const { deptKey } = useParams({ strict: false }) as { deptKey: string };
  const navigate = useNavigate();
  const { tab } = Route.useSearch();

  React.useEffect(() => {
    trackRecentDepartment(deptKey);
  }, [deptKey]);

  const detailQ = useQuery({
    queryKey: departmentsKeys.detail(deptKey),
    queryFn: () => departmentsApi.get(deptKey),
    refetchInterval: 20_000,
  });
  const listQ = useQuery({
    queryKey: departmentsKeys.list(),
    queryFn: () => departmentsApi.list(),
  });

  const dept = detailQ.data;
  const departments = listQ.data?.departments ?? [];

  useDepartmentEvents(dept?.id, { deptKey });

  if (detailQ.isLoading || !dept) {
    return (
      <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground/70">
        Loading department…
      </div>
    );
  }
  if (detailQ.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="text-[13px] text-destructive">Could not load this department.</p>
        <Link to="/departments" className="text-[12px] text-muted-foreground underline">
          Back to overview
        </Link>
      </div>
    );
  }

  const onTabChange = (next: DeptTab): void => {
    void navigate({ to: '/departments/$deptKey', params: { deptKey }, search: { tab: next } });
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-col gap-2 border-b border-border px-6 pt-4 pb-3">
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <Link to="/departments" className="hover:text-foreground">
            Departments
          </Link>
          <span aria-hidden>/</span>
          <span className="text-foreground">{dept.displayName}</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2">
              <span
                role="img"
                aria-label={STATE_LABEL[dept.health]}
                className={cn('h-2 w-2 shrink-0 rounded-full', STATE_DOT[dept.health])}
              />
              <h1 className="truncate text-[18px] font-semibold tracking-tight text-foreground">
                {dept.displayName}
              </h1>
              <span
                className={cn(
                  'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                  dept.health === 'green' && 'bg-success/15 text-success',
                  dept.health === 'yellow' && 'bg-warning/15 text-warning',
                  dept.health === 'red' && 'bg-destructive/15 text-destructive',
                  dept.health === 'unknown' && 'bg-muted text-muted-foreground',
                )}
              >
                {STATE_LABEL[dept.health]}
              </span>
            </div>
            {dept.mission ? (
              <p className="max-w-2xl text-[12px] text-muted-foreground">{dept.mission}</p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <HandoffForm
              departments={departments}
              sourceKey={dept.key}
              trigger={
                <Button size="sm" variant="outline">
                  <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden />
                  Handoff
                </Button>
              }
            />
            <EscalationForm
              departmentKey={dept.key}
              trigger={
                <Button size="sm" variant="outline">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                  Escalate
                </Button>
              }
            />
          </div>
        </div>

        <div role="tablist" className="-mb-[1px] mt-2 flex gap-3 text-[12px]">
          {TABS.map((t) => (
            <button
              key={t.value}
              role="tab"
              type="button"
              aria-selected={tab === t.value}
              onClick={() => onTabChange(t.value)}
              className={cn(
                'border-b-2 pb-2 transition-colors',
                tab === t.value
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4">
        {tab === 'overview' ? <OverviewTab deptKey={deptKey} dept={dept} /> : null}
        {tab === 'queue' ? <QueueTab deptKey={deptKey} /> : null}
        {tab === 'handoffs' ? <HandoffsTab deptKey={deptKey} /> : null}
        {tab === 'escalations' ? <EscalationsTab deptKey={deptKey} /> : null}
        {tab === 'settings' ? <SettingsTab dept={dept} /> : null}
      </div>
    </div>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────

interface OverviewTabProps {
  deptKey: string;
  dept: NonNullable<
    ReturnType<typeof useQuery<Awaited<ReturnType<typeof departmentsApi.get>>>>['data']
  >;
}

function OverviewTab({ deptKey, dept }: OverviewTabProps) {
  const queueQ = useQuery({
    queryKey: departmentsKeys.queue(deptKey, { limit: 10 }),
    queryFn: () => departmentsApi.listQueue(deptKey, { limit: 10 }),
  });

  const recentActivity: DepartmentActivity[] = (queueQ.data?.items ?? []).map((item) => ({
    kind: 'queue_item',
    id: item.id,
    title: item.title,
    detail: null,
    status: item.status,
    occurredAt: item.updatedAt,
  }));

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Snapshot
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            label="Pending"
            value={fmtCount(dept.pendingItems)}
            hint="Standing queue depth"
            tone={dept.pendingItems > 0 ? 'warning' : 'muted'}
          />
          <KpiTile
            label="In progress"
            value={fmtCount(dept.inProgressItems)}
            hint="Currently executing"
          />
          <KpiTile
            label="Throughput / 24h"
            value={fmtCount(dept.throughput24h)}
            hint="Items processed"
            tone={dept.throughput24h > 0 ? 'success' : 'muted'}
          />
          <KpiTile
            label="Error rate / 1h"
            value={fmtPercent(dept.errorRate1h)}
            hint={`Last processed ${fmtRelative(dept.lastProcessedAt)}`}
            tone={dept.errorRate1h > 0.5 ? 'error' : dept.errorRate1h > 0.2 ? 'warning' : 'muted'}
          />
        </div>
      </section>

      {dept.reasons.length > 0 ? (
        <section>
          <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Health signals
          </h2>
          <ul className="flex flex-col gap-1.5">
            {dept.reasons.map((reason, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: reasons are deterministic per snapshot
                key={i}
                className="rounded border-l-2 border-border bg-muted/30 px-3 py-1.5 text-[12px] text-muted-foreground"
              >
                {reason}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section>
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Recent activity
        </h2>
        <StewardChatPane
          activity={recentActivity}
          emptyHint="No recent activity in this department."
        />
      </section>
    </div>
  );
}

// ── Queue tab ─────────────────────────────────────────────────────────

const STATUS_DOT: Record<QueueItem['status'], string> = {
  pending: 'bg-warning',
  in_progress: 'bg-foreground/60',
  done: 'bg-success',
  error: 'bg-destructive',
  timeout: 'bg-destructive',
  cancelled: 'bg-muted-foreground/40',
};

const PRIORITY_LABEL: Record<QueueItem['priority'], string> = {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

function QueueTab({ deptKey }: { deptKey: string }) {
  const [statusFilter, setStatusFilter] = React.useState<QueueItem['status'] | 'all'>('all');
  const params =
    statusFilter === 'all'
      ? ({ limit: 100 } as const)
      : ({ limit: 100, status: statusFilter } as const);
  const queueQ = useQuery({
    queryKey: departmentsKeys.queue(deptKey, params),
    queryFn: () => departmentsApi.listQueue(deptKey, params),
    refetchInterval: 10_000,
  });

  const items = queueQ.data?.items ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 text-[12px]">
        {(['all', 'pending', 'in_progress', 'done', 'error'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={cn(
              'rounded px-2 py-1 transition-colors',
              statusFilter === s
                ? 'bg-foreground/8 text-foreground'
                : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
            )}
          >
            {s === 'all' ? 'All' : s.replace('_', ' ')}
          </button>
        ))}
        <div className="flex-1" />
        <span className="text-[11px] text-muted-foreground">
          {fmtCount(queueQ.data?.total ?? 0)} total
        </span>
      </div>

      {queueQ.isLoading ? (
        <p className="text-[12px] text-muted-foreground/70">Loading queue…</p>
      ) : items.length === 0 ? (
        <p className="text-[12px] text-muted-foreground/70">No queue items.</p>
      ) : (
        <ol className="flex flex-col divide-y divide-border/70 rounded border border-border">
          {items.map((item) => (
            <li
              key={item.id}
              className="group/row flex items-start gap-3 px-3 py-2 transition-colors hover:bg-muted/40"
            >
              <span
                aria-hidden
                className={cn('mt-1 h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT[item.status])}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[13px] text-foreground">{item.title}</span>
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="uppercase tracking-wider">{item.status.replace('_', ' ')}</span>
                  <span aria-hidden>·</span>
                  <span>{PRIORITY_LABEL[item.priority]}</span>
                  <span aria-hidden>·</span>
                  <span title={item.updatedAt}>{fmtRelative(item.updatedAt)}</span>
                </div>
                {item.errorMessage ? (
                  <span className="mt-0.5 text-[11px] text-destructive">{item.errorMessage}</span>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ── Handoffs tab ──────────────────────────────────────────────────────

function HandoffsTab({ deptKey }: { deptKey: string }) {
  // The upstream service exposes handoffs only via /handoffs/:id (one at
  // a time). We surface incoming handoffs by reading the standing queue
  // and surfacing items whose payload carries a handoff correlation;
  // outgoing handoffs come from the gateway's audit row on the response
  // of the most recent POST. For v1 we render queue items that carry
  // a handoff origin in their correlation_id, plus a button to send a
  // new handoff (HandoffForm available from header).
  const queueQ = useQuery({
    queryKey: departmentsKeys.queue(deptKey, { limit: 50 }),
    queryFn: () => departmentsApi.listQueue(deptKey, { limit: 50 }),
  });
  const handoffOriginated = (queueQ.data?.items ?? []).filter(
    (item) => item.correlationId !== null,
  );

  return (
    <div className="flex flex-col gap-4">
      <section>
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Incoming handoffs (recent)
        </h2>
        {queueQ.isLoading ? (
          <p className="text-[12px] text-muted-foreground/70">Loading…</p>
        ) : handoffOriginated.length === 0 ? (
          <p className="text-[12px] text-muted-foreground/70">
            No handoffs landed in this department recently.
          </p>
        ) : (
          <ol className="flex flex-col divide-y divide-border/70 rounded border border-border">
            {handoffOriginated.map((item) => (
              <li
                key={item.id}
                className="flex items-start gap-3 px-3 py-2 transition-colors hover:bg-muted/40"
              >
                <span
                  aria-hidden
                  className={cn('mt-1 h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT[item.status])}
                />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[13px] text-foreground">{item.title}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {item.status.replace('_', ' ')} · {fmtRelative(item.updatedAt)}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Send a handoff
        </h2>
        <p className="text-[12px] text-muted-foreground/80">
          Use the <strong>Handoff</strong> button in the header to push work to another department.
          Cross-department contracts gate which payloads are accepted.
        </p>
      </section>
    </div>
  );
}

// ── Escalations tab ───────────────────────────────────────────────────

function EscalationsTab({ deptKey }: { deptKey: string }) {
  // We don't have a list-escalations endpoint upstream yet; for v1 the
  // UI surfaces the "Open an escalation" affordance + a placeholder
  // pointing to /api/escalations/:id once we have a list endpoint.
  return (
    <div className="flex flex-col gap-4">
      <section>
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Escalations for this department
        </h2>
        <p className="text-[12px] text-muted-foreground/80">
          Open escalations are recorded as audit rows on the
          <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[11px]">
            chaos.department_escalations
          </code>
          table. The list-view lands in a follow-up phase; in the meantime use the{' '}
          <strong>Escalate</strong> button in the header to open one. Each new escalation triggers a
          real-time NOTIFY that refreshes this page.
        </p>
        <EscalationForm
          departmentKey={deptKey}
          trigger={
            <Button size="sm" className="mt-2">
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              New escalation
            </Button>
          }
        />
      </section>
    </div>
  );
}

// ── Settings tab ──────────────────────────────────────────────────────

function SettingsTab({
  dept,
}: {
  dept: NonNullable<
    ReturnType<typeof useQuery<Awaited<ReturnType<typeof departmentsApi.get>>>>['data']
  >;
}) {
  const contractsQ = useQuery({
    queryKey: departmentsKeys.handoffContracts(),
    queryFn: () => departmentsApi.listHandoffContracts(),
  });
  const inbound = (contractsQ.data ?? []).filter((c) => c.target === dept.key);
  const outbound = (contractsQ.data ?? []).filter((c) => c.source === dept.key);

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Identity
        </h2>
        <dl className="grid gap-2 sm:grid-cols-2">
          <div>
            <dt className="text-[11px] text-muted-foreground">Key</dt>
            <dd className="font-mono text-[12px] text-foreground">{dept.key}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-muted-foreground">Workflow cadence</dt>
            <dd className="text-[12px] text-foreground">{dept.cadenceSeconds}s</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-[11px] text-muted-foreground">Default skills</dt>
            <dd className="flex flex-wrap gap-1.5 pt-1">
              {dept.defaultSkillKeys.length === 0 ? (
                <span className="text-[12px] text-muted-foreground/70">No defaults.</span>
              ) : (
                dept.defaultSkillKeys.map((skill) => (
                  <span
                    key={skill}
                    className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
                  >
                    {skill}
                  </span>
                ))
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section>
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Inbound handoff contracts ({inbound.length})
        </h2>
        {inbound.length === 0 ? (
          <p className="text-[12px] text-muted-foreground/70">No registered inbound contracts.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {inbound.map((c) => (
              <li
                key={c.name}
                className="rounded border border-border bg-background px-3 py-2 text-[12px]"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono">{c.name}</span>
                  <span className="text-[11px] text-muted-foreground">
                    from <strong>{c.source}</strong>
                  </span>
                </div>
                {c.description ? (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{c.description}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Outbound handoff contracts ({outbound.length})
        </h2>
        {outbound.length === 0 ? (
          <p className="text-[12px] text-muted-foreground/70">No registered outbound contracts.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {outbound.map((c) => (
              <li
                key={c.name}
                className="rounded border border-border bg-background px-3 py-2 text-[12px]"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono">{c.name}</span>
                  <span className="text-[11px] text-muted-foreground">
                    to <strong>{c.target}</strong>
                  </span>
                </div>
                {c.description ? (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{c.description}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/departments/$deptKey')({
  component: DepartmentDetailPage,
  validateSearch: (search) => deptSearchSchema.parse(search),
});
